import { useMemo, useState } from "react";
import { Link } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCreateCheckin,
  useGetSquad,
  useListCheckins,
  useListMembers,
  useListSchedules,
  getGetCurrentUserQueryKey,
  getListCheckinsQueryKey,
  getListMembersQueryKey,
  getListSchedulesQueryKey,
} from "@workspace/api-client-react";
import type { Checkin, Member, ScheduleEntry } from "@workspace/api-client-react";
import { cn } from "@/lib/utils";
import { localDateStr } from "@/lib/localDate";
import { WEEKDAYS, dateForProgramDay, programAnchorMonday, programDayFor } from "@/lib/programWeek";
import { useRunnerView } from "@/hooks/useRunnerView";

// WI#23 (S2-S3) — the runner's home. Built from the approved prototype
// (attached_assets/peakrunning-runner-experience-prototype-20260921.html). Adoption bar: a runner
// gets from invite to first logged run in four steps, and this page works one-handed on a phone.

const RUN_TYPES = ["Easy Run", "Tempo", "Long Run", "Intervals", "Cross-train", "Other"] as const;

type Plan = { kind: "run"; entry: ScheduleEntry } | { kind: "rest" } | { kind: "none" };

function planFor(entries: ScheduleEntry[], memberId: number, week: number, day: string): Plan {
  const today = entries.filter(
    (e) => (e.memberId == null || e.memberId === memberId) && e.weekNumber === week && e.dayOfWeek === day,
  );
  const run = today.find((e) => e.sessionType.toLowerCase() !== "rest");
  if (run) return { kind: "run", entry: run };
  if (today.length > 0) return { kind: "rest" };
  return { kind: "none" };
}

function Chips({ value, onChange }: { value: string | null; onChange: (v: string) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {RUN_TYPES.map((t) => (
        <button
          key={t}
          type="button"
          onClick={() => onChange(t)}
          className={cn(
            "px-3 py-2 rounded-full text-sm border transition-colors",
            value === t ? "bg-foreground text-background border-foreground font-semibold" : "border-border hover:border-foreground/40",
          )}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

function HowItWent({ value, onChange }: { value: "crushed" | "skipped" | null; onChange: (v: "crushed" | "skipped") => void }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <button
        type="button"
        onClick={() => onChange("crushed")}
        className={cn(
          "rounded p-5 text-left border-2 transition-colors",
          value === "crushed" ? "border-foreground bg-primary" : "border-border hover:border-foreground/40",
        )}
      >
        <div className="font-display text-xl font-bold">CRUSHED IT</div>
        <div className="text-sm">Showed up. Did the work.</div>
      </button>
      <button
        type="button"
        onClick={() => onChange("skipped")}
        className={cn(
          "rounded p-5 text-left border-2 transition-colors",
          value === "skipped" ? "border-foreground bg-card" : "border-border hover:border-foreground/40",
        )}
      >
        <div className={cn("font-display text-xl font-bold", value !== "skipped" && "text-muted-foreground")}>SKIPPED</div>
        <div className={cn("text-sm", value !== "skipped" && "text-muted-foreground")}>Honest. Your squad sees this.</div>
      </button>
    </div>
  );
}

function StreakBadge({ days }: { days: number }) {
  return (
    <div className="text-right shrink-0">
      <div className="font-display text-3xl font-bold">
        {days}
        <span className="text-base">d</span>
      </div>
      <div className="text-xs font-mono uppercase text-muted-foreground">streak</div>
    </div>
  );
}

function TodayCard({
  me,
  squadId,
  plan,
  today,
  logged,
  nextUp,
  squadCheckedIn,
  squadSize,
  coachName,
}: {
  me: Member;
  squadId: number;
  plan: Plan;
  today: string;
  logged: Checkin | undefined;
  nextUp: string | null;
  squadCheckedIn: number;
  squadSize: number;
  coachName: string | undefined;
}) {
  const queryClient = useQueryClient();
  const createCheckin = useCreateCheckin();
  const [status, setStatus] = useState<"crushed" | "skipped" | null>(null);
  const [note, setNote] = useState("");
  const [different, setDifferent] = useState(false);
  const [runType, setRunType] = useState<string | null>(null);
  const [miles, setMiles] = useState("");
  const [loggingOnRest, setLoggingOnRest] = useState(false);
  const [error, setError] = useState("");

  const selfLog = plan.kind === "none" || (plan.kind === "rest" && loggingOnRest);
  const describesRun = selfLog || different;
  const canLog = !!status && (!selfLog || !!runType) && !createCheckin.isPending;
  const milesNumber = miles.trim() === "" ? null : Number.parseFloat(miles);

  const submit = () => {
    if (!canLog || !status) return;
    if (milesNumber !== null && (Number.isNaN(milesNumber) || milesNumber < 0 || milesNumber > 200)) {
      setError("Distance should be a number of miles between 0 and 200.");
      return;
    }
    setError("");
    createCheckin.mutate(
      {
        data: {
          memberId: me.id,
          squadId,
          status,
          note: note.trim() || null,
          checkinDate: today,
          ...(describesRun ? { sessionType: runType, miles: milesNumber } : {}),
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListCheckinsQueryKey({ squadId }) });
          queryClient.invalidateQueries({ queryKey: getListMembersQueryKey({ squadId }) });
          queryClient.invalidateQueries({ queryKey: getGetCurrentUserQueryKey() });
        },
        onError: () => setError("That didn't save. Check your connection and try again."),
      },
    );
  };

  const card = "bg-background border border-border border-t-4 border-t-primary rounded p-5 sm:p-6 shadow-lg";

  if (logged) {
    const what = logged.sessionType ?? (plan.kind === "run" ? plan.entry.sessionType : "Run");
    const dist = logged.miles ?? (plan.kind === "run" ? plan.entry.targetMiles : null);
    const streak = me.currentStreak;
    return (
      <div className={card}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="pr-label">
              Today · {what}
              {dist ? ` · ${dist} mi` : ""}
            </div>
            <div className="font-display text-3xl font-bold">
              {logged.status === "crushed" ? "Crushed it" : "Skipped"}{" "}
              {logged.status === "crushed" && <span className="inline-block align-middle bg-primary text-base px-2 rounded-sm">✓</span>}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Logged {new Date(logged.createdAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
              {logged.note ? <> · <i>"{logged.note}"</i></> : null}
            </p>
          </div>
          <StreakBadge days={streak} />
        </div>
        {logged.status === "crushed" && (
          <div className="mt-5 rounded bg-accent/40 px-4 py-3 text-sm">
            <b>
              {streak > 1
                ? `${streak} days. Keep it going.`
                : me.longestStreak <= 1
                  ? "1-day streak, your first!"
                  : "Day 1. Back at it."}
            </b>{" "}
            {streak <= 1 ? "Come back tomorrow to make it 2." : ""}
            {nextUp ? <> Next up: <b>{nextUp}</b>.</> : null}
          </div>
        )}
        <div className="mt-3 text-sm text-muted-foreground">
          You're {ordinal(squadCheckedIn)} of {squadSize} on your squad to check in today.
        </div>
      </div>
    );
  }

  if (plan.kind === "rest" && !loggingOnRest) {
    return (
      <div className={card}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="pr-label">Today{coachName ? ` · from ${coachName} (coach)` : ""}</div>
            <div className="font-display text-2xl font-bold">Rest day</div>
            <p className="text-sm text-muted-foreground mt-1">Recovery is training too. No check-in needed, and your streak is safe.</p>
          </div>
          <StreakBadge days={me.currentStreak} />
        </div>
        {nextUp && <div className="mt-4 text-sm">Next up: <b>{nextUp}</b></div>}
        <button
          type="button"
          onClick={() => setLoggingOnRest(true)}
          className="mt-4 text-xs font-semibold text-foreground underline underline-offset-2 hover:text-muted-foreground"
        >
          Ran anyway? Log it
        </button>
      </div>
    );
  }

  return (
    <div className={card}>
      <div className="flex items-start justify-between gap-4">
        <div>
          {plan.kind === "run" ? (
            <>
              <div className="pr-label">Today's session{coachName ? ` · from ${coachName} (coach)` : ""}</div>
              <div className="font-display text-2xl font-bold">
                {plan.entry.sessionType}
                {plan.entry.targetMiles ? (
                  <span className="inline-block align-middle ml-2 bg-primary text-sm font-mono font-bold px-2 py-0.5 rounded-sm">
                    {plan.entry.targetMiles} mi
                  </span>
                ) : null}
              </div>
              {plan.entry.description && <p className="text-sm text-muted-foreground mt-1">{plan.entry.description}</p>}
            </>
          ) : (
            <>
              <div className="pr-label">Today</div>
              <div className="font-display text-2xl font-bold">No session planned</div>
              <p className="text-sm text-muted-foreground mt-1">Log what you did. Any run counts, and no coach needed.</p>
            </>
          )}
        </div>
        <StreakBadge days={me.currentStreak} />
      </div>

      {plan.kind === "run" && !different && (
        <button
          type="button"
          onClick={() => setDifferent(true)}
          className="mt-4 text-xs font-semibold text-foreground underline underline-offset-2 hover:text-muted-foreground"
        >
          Did something different?
        </button>
      )}

      {describesRun && (
        <div className={cn(plan.kind === "run" && "rounded border border-border px-4 py-3")} style={{ marginTop: 24 }}>
          <div className="pr-label">{plan.kind === "run" ? "What you did instead" : "What did you do?"}</div>
          <Chips value={runType} onChange={setRunType} />
          <div style={{ marginTop: 24 }}>
            <label className="pr-label" htmlFor="today-miles">Distance (optional)</label>
            <input
              id="today-miles"
              className="pr-field"
              inputMode="decimal"
              placeholder="e.g. 4"
              value={miles}
              onChange={(e) => setMiles(e.target.value)}
            />
          </div>
        </div>
      )}

      <div style={{ marginTop: 24 }}>
        <div className="pr-label">How did it go?</div>
        <HowItWent value={status} onChange={setStatus} />
      </div>
      <input
        className="pr-field"
        style={{ marginTop: 12 }}
        placeholder="Add a note (optional): felt strong, legs tired…"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      {error && <p className="text-destructive text-sm mt-3">{error}</p>}
      <button
        type="button"
        onClick={submit}
        disabled={!canLog}
        className={cn(
          "mt-3 w-full rounded py-3.5 text-center text-sm font-semibold transition-opacity",
          canLog ? "bg-primary text-primary-foreground hover:opacity-90" : "bg-primary/30 text-foreground/40",
        )}
      >
        {createCheckin.isPending
          ? "Saving…"
          : !status || (selfLog && !runType)
            ? selfLog
              ? "Pick what you did and how it went"
              : "Pick how it went to log today"
            : status === "skipped"
              ? "Log skipped day"
              : different
                ? "Log what I did"
                : "Log today's run"}
      </button>
      {me.currentStreak === 0 && <div className="mt-3 text-sm text-muted-foreground">Your first check-in starts your streak.</div>}
    </div>
  );
}

function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]!);
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="bg-card border border-border rounded p-4">
      <div className="font-display text-2xl sm:text-3xl font-bold">{value}</div>
      <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

export default function Today() {
  const { me, isLoading: meLoading } = useRunnerView();
  const squadId = me?.squadId ?? undefined;
  const enabled = !!squadId;
  const { data: squad } = useGetSquad(squadId ?? 0, { query: { enabled, queryKey: [["squads", squadId]] } });
  const { data: schedules } = useListSchedules({ squadId }, { query: { enabled, queryKey: getListSchedulesQueryKey({ squadId }) } });
  const { data: checkins } = useListCheckins({ squadId }, { query: { enabled, queryKey: getListCheckinsQueryKey({ squadId }) } });
  const { data: members } = useListMembers({ squadId }, { query: { enabled, queryKey: getListMembersQueryKey({ squadId }) } });

  const today = localDateStr();
  const model = useMemo(() => {
    if (!me || !squad) return null;
    const anchor = programAnchorMonday(squad.createdAt);
    const { week, day } = programDayFor(anchor, today);
    const entries = schedules ?? [];
    const plan = planFor(entries, me.id, week, day);
    const mine = (checkins ?? []).filter((c) => c.memberId === me.id);
    const logged = mine.find((c) => c.checkinDate === today);
    const weekDays = WEEKDAYS.map((d) => {
      const date = dateForProgramDay(anchor, week, d);
      return { day: d, date, plan: planFor(entries, me.id, week, d), checkin: mine.find((c) => c.checkinDate === date) };
    });
    const plannedRuns = weekDays.filter((w) => w.plan.kind === "run").length;
    const doneRuns = weekDays.filter((w) => w.checkin?.status === "crushed").length;
    const upcoming = weekDays.find((w) => w.date > today && w.plan.kind === "run");
    const nextUp =
      upcoming && upcoming.plan.kind === "run"
        ? `${upcoming.day.slice(0, 3)} · ${upcoming.plan.entry.sessionType}${upcoming.plan.entry.targetMiles ? ` · ${upcoming.plan.entry.targetMiles} mi` : ""}`
        : null;
    const todays = (checkins ?? []).filter((c) => c.checkinDate === today);
    return { plan, logged, weekDays, plannedRuns, doneRuns, nextUp, todays };
  }, [me, squad, schedules, checkins, today]);

  if (meLoading || !me || !squad || !model) {
    return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading…</div>;
  }

  const roster = members ?? [];
  const coach = roster.find((m) => m.role === "captain");
  const nameOf = (id: number) => roster.find((m) => m.id === id)?.name ?? "A runner";
  const board = [...roster].sort((a, b) => b.currentStreak - a.currentStreak);
  const firstName = me.name.split(/\s+/)[0];
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Morning" : hour < 18 ? "Afternoon" : "Evening";
  const dateLabel = new Date().toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
          {dateLabel} · {squad.name}
        </div>
        <h1 className="font-display text-3xl font-bold mt-1">
          {greeting}, {firstName}.
        </h1>
      </div>

      <TodayCard
        me={me}
        squadId={squad.id}
        plan={model.plan}
        today={today}
        logged={model.logged}
        nextUp={model.nextUp}
        squadCheckedIn={model.todays.length}
        squadSize={roster.length}
        coachName={coach?.name.split(/\s+/)[0]}
      />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat value={`${me.currentStreak}d`} label="Current streak" />
        <Stat value={`${me.longestStreak}d`} label="Longest" />
        <Stat value={me.adherencePct ? `${me.adherencePct}%` : "—"} label="Adherence" />
        <Stat value={`${model.doneRuns} / ${model.plannedRuns}`} label="This week" />
      </div>

      <div className="bg-background border border-border rounded p-4 sm:p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">My week</h2>
        </div>
        <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
          {model.weekDays.map((w) => (
            <div
              key={w.day}
              className={cn(
                "rounded p-1.5 sm:p-2 min-h-[72px] bg-background",
                w.date === today ? "border-2 border-foreground" : "border border-border",
              )}
            >
              <div className="text-[10px] sm:text-xs font-mono uppercase text-muted-foreground">{w.day.slice(0, 3)}</div>
              {w.plan.kind === "run" ? (
                <>
                  <div className="text-[11px] sm:text-xs font-semibold mt-1 leading-tight">{w.plan.entry.sessionType}</div>
                  {w.plan.entry.targetMiles ? (
                    <span className="inline-block mt-1 bg-primary text-[10px] sm:text-xs font-mono font-bold px-1 py-0.5 rounded-sm">
                      {w.plan.entry.targetMiles}mi
                    </span>
                  ) : null}
                </>
              ) : w.plan.kind === "rest" ? (
                <div className="text-[11px] sm:text-xs font-semibold mt-1">Rest</div>
              ) : (
                <div className="text-xs text-muted-foreground mt-1">—</div>
              )}
              {w.checkin && (
                <div className={cn("text-[10px] font-mono mt-1", w.checkin.status === "crushed" ? "text-foreground" : "text-destructive")}>
                  {w.checkin.status === "crushed" ? "✓ done" : "skipped"}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="bg-background border border-border rounded">
          <div className="px-5 py-4 border-b border-border">
            <h2 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">Squad today</h2>
          </div>
          {model.todays.length === 0 ? (
            <div className="px-5 py-3 text-sm text-muted-foreground">No one has checked in yet today.</div>
          ) : (
            model.todays.slice(0, 5).map((c) => (
              <div key={c.id} className="px-5 py-3 text-sm border-b border-border last:border-b-0">
                <b>{nameOf(c.memberId)}</b> {c.status === "crushed" ? "crushed it" : "skipped"}
                {c.note ? <>: <i className="text-muted-foreground">{c.note}</i></> : null}
              </div>
            ))
          )}
          <div className="px-5 py-3 text-sm text-muted-foreground border-t border-border">
            {model.todays.length} of {roster.length} runners checked in today
          </div>
        </div>
        <div className="bg-background border border-border rounded">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <h2 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">Streak board</h2>
            <Link href={`/squads/${squad.id}`} className="text-xs font-semibold underline underline-offset-2">
              My squad
            </Link>
          </div>
          {board.slice(0, 5).map((m, i) => (
            <div
              key={m.id}
              className={cn("px-5 py-3 text-sm flex justify-between border-b border-border last:border-b-0", m.id === me.id && "bg-accent/40")}
            >
              <span>
                {i + 1} · {m.id === me.id ? <b>You</b> : m.name}
              </span>
              <b>{m.currentStreak}d</b>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
